import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FolderIcon,
  DocumentIcon,
  PhotoIcon,
  VideoCameraIcon,
  MusicalNoteIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  TrashIcon,
  PencilIcon,
  ArrowDownTrayIcon,
  EyeIcon,
  FolderPlusIcon,
  CloudArrowUpIcon,
  XMarkIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  DocumentDuplicateIcon,
  LinkIcon,
  StarIcon,
  ClockIcon,
  TagIcon,
  FunnelIcon,
  Squares2X2Icon,
  ListBulletIcon,
  CheckIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';
import api from '../services/api';
import { useTheme } from '../context/ThemeContext';

// Theme-aware styles generator
const getStyles = (isDark) => ({
  container: {
    padding: '0',
    backgroundColor: 'transparent',
    minHeight: 'auto'
  },
  breadcrumbNav: {
    marginBottom: '1rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    flexWrap: 'wrap'
  },
  breadcrumbBtn: {
    background: 'none',
    border: 'none',
    color: isDark ? '#60a5fa' : '#2563eb',
    cursor: 'pointer',
    fontSize: '0.875rem',
    padding: 0,
    fontWeight: '500'
  },
  breadcrumbSeparator: {
    color: isDark ? '#6b7280' : '#9ca3af',
    margin: '0 0.25rem'
  },
  breadcrumbCurrent: {
    color: isDark ? '#9ca3af' : '#6b7280',
    fontSize: '0.875rem'
  },
  card: {
    backgroundColor: isDark ? '#1f2937' : '#ffffff',
    borderRadius: '8px',
    border: `1px solid ${isDark ? '#374151' : '#e5e7eb'}`,
    padding: 'var(--space-4)',
    boxShadow: 'var(--shadow-sm)',
    overflow: 'hidden'
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1.5rem',
    paddingBottom: '1.5rem',
    borderBottom: '3px solid #EF4444'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
    flexWrap: 'wrap',
    gap: '16px'
  },
  title: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: isDark ? '#f9fafb' : '#111827',
    margin: '0 0 0.25rem 0'
  },
  subtitle: {
    fontSize: '0.875rem',
    color: isDark ? '#9ca3af' : '#6b7280',
    margin: 0
  },
  headerActions: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center'
  },
  btn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 16px',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s',
    border: 'none'
  },
  btnPrimary: {
    backgroundColor: '#ef4444',
    color: 'white'
  },
  btnSecondary: {
    backgroundColor: isDark ? '#374151' : '#f3f4f6',
    color: isDark ? '#f9fafb' : '#111827',
    border: `1px solid ${isDark ? '#4b5563' : '#e5e7eb'}`
  },
  btnIcon: {
    padding: '8px',
    borderRadius: '6px',
    backgroundColor: isDark ? '#374151' : '#f3f4f6',
    border: `1px solid ${isDark ? '#4b5563' : '#e5e7eb'}`,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  mainContent: {
    display: 'grid',
    gridTemplateColumns: '280px 1fr',
    gap: '24px'
  },
  sidebar: {
    backgroundColor: isDark ? '#1f2937' : '#ffffff',
    borderRadius: '12px',
    padding: '16px',
    border: `1px solid ${isDark ? '#374151' : '#e5e7eb'}`,
    height: 'fit-content'
  },
  sidebarSection: {
    marginBottom: '24px'
  },
  sidebarTitle: {
    fontSize: '12px',
    fontWeight: '600',
    color: isDark ? '#9ca3af' : '#6b7280',
    textTransform: 'uppercase',
    marginBottom: '12px'
  },
  folderItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    color: isDark ? '#f9fafb' : '#111827',
    fontSize: '14px'
  },
  folderItemActive: {
    backgroundColor: '#ef4444',
    color: 'white'
  },
  folderItemHover: {
    backgroundColor: isDark ? '#374151' : '#f3f4f6'
  },
  folderIcon: {
    width: '18px',
    height: '18px'
  },
  folderCount: {
    marginLeft: 'auto',
    fontSize: '12px',
    color: isDark ? '#9ca3af' : '#6b7280',
    backgroundColor: isDark ? '#374151' : '#f3f4f6',
    padding: '2px 8px',
    borderRadius: '10px'
  },
  contentArea: {
    backgroundColor: isDark ? '#1f2937' : '#ffffff',
    borderRadius: '12px',
    border: `1px solid ${isDark ? '#374151' : '#e5e7eb'}`,
    overflow: 'hidden'
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 0',
    borderBottom: `1px solid ${isDark ? '#374151' : '#e5e7eb'}`,
    backgroundColor: 'transparent',
    flexWrap: 'wrap',
    gap: '12px'
  },
  searchContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    backgroundColor: 'var(--bg-secondary)',
    borderRadius: '8px',
    padding: '8px 12px',
    border: '1px solid var(--border-color)',
    flex: '1',
    maxWidth: '400px'
  },
  searchInput: {
    border: 'none',
    background: 'none',
    outline: 'none',
    color: 'var(--text-primary)',
    fontSize: '14px',
    width: '100%'
  },
  toolbarActions: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center'
  },
  viewToggle: {
    display: 'flex',
    backgroundColor: 'var(--bg-secondary)',
    borderRadius: '6px',
    padding: '4px',
    border: '1px solid var(--border-color)'
  },
  viewToggleBtn: {
    padding: '6px',
    borderRadius: '4px',
    cursor: 'pointer',
    backgroundColor: 'transparent',
    border: 'none',
    color: 'var(--text-secondary)'
  },
  viewToggleBtnActive: {
    backgroundColor: 'var(--accent-primary)',
    color: 'white'
  },
  breadcrumb: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 16px',
    borderBottom: `1px solid ${isDark ? '#374151' : '#e5e7eb'}`,
    fontSize: '14px',
    color: isDark ? '#9ca3af' : '#6b7280',
    backgroundColor: isDark ? '#111827' : '#f9fafb'
  },
  breadcrumbItem: {
    cursor: 'pointer',
    color: '#EF4444',
    transition: 'color 0.2s',
    fontWeight: '500'
  },
  breadcrumbItemHover: {
    textDecoration: 'underline'
  },
  breadcrumbItemActive: {
    color: isDark ? '#f3f4f6' : '#111827',
    fontWeight: '600',
    cursor: 'default'
  },
  breadcrumbSeparator: {
    color: isDark ? '#4b5563' : '#9ca3af'
  },
  contentGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
    gap: '16px',
    padding: '16px'
  },
  contentList: {
    padding: '0'
  },
  fileCard: {
    backgroundColor: 'var(--bg-primary)',
    borderRadius: '10px',
    border: '1px solid var(--border-color)',
    overflow: 'hidden',
    cursor: 'pointer',
    transition: 'all 0.2s',
    position: 'relative'
  },
  fileCardSelected: {
    borderColor: 'var(--accent-primary)',
    boxShadow: '0 0 0 2px rgba(var(--accent-primary-rgb), 0.2)'
  },
  filePreview: {
    height: '120px',
    backgroundColor: 'var(--bg-tertiary)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative'
  },
  filePreviewImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  },
  fileTypeIcon: {
    width: '48px',
    height: '48px',
    color: 'var(--text-secondary)'
  },
  fileInfo: {
    padding: '12px'
  },
  fileName: {
    fontSize: '14px',
    fontWeight: '500',
    color: 'var(--text-primary)',
    marginBottom: '4px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  fileMeta: {
    fontSize: '12px',
    color: 'var(--text-secondary)',
    display: 'flex',
    gap: '8px'
  },
  fileActions: {
    position: 'absolute',
    top: '8px',
    right: '8px',
    display: 'flex',
    gap: '4px',
    opacity: 0,
    transition: 'opacity 0.2s'
  },
  fileActionBtn: {
    padding: '6px',
    borderRadius: '4px',
    backgroundColor: 'rgba(0,0,0,0.6)',
    border: 'none',
    cursor: 'pointer',
    color: 'white'
  },
  starBtn: {
    position: 'absolute',
    top: '8px',
    left: '8px',
    padding: '4px',
    backgroundColor: 'rgba(0,0,0,0.6)',
    borderRadius: '4px',
    border: 'none',
    cursor: 'pointer'
  },
  listItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '12px 16px',
    borderBottom: '1px solid var(--border-color)',
    cursor: 'pointer',
    transition: 'background-color 0.2s'
  },
  listItemHover: {
    backgroundColor: 'var(--bg-tertiary)'
  },
  listItemSelected: {
    backgroundColor: 'rgba(var(--accent-primary-rgb), 0.1)'
  },
  listItemIcon: {
    width: '40px',
    height: '40px',
    borderRadius: '8px',
    backgroundColor: 'var(--bg-tertiary)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  listItemInfo: {
    flex: 1,
    minWidth: 0
  },
  listItemName: {
    fontSize: '14px',
    fontWeight: '500',
    color: 'var(--text-primary)',
    marginBottom: '2px'
  },
  listItemMeta: {
    fontSize: '12px',
    color: 'var(--text-secondary)'
  },
  listItemActions: {
    display: 'flex',
    gap: '8px'
  },
  modal: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  modalContent: {
    backgroundColor: 'var(--bg-primary)',
    borderRadius: '12px',
    width: '100%',
    maxWidth: '600px',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid var(--border-color)'
  },
  modalTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: 'var(--text-primary)',
    margin: 0
  },
  modalBody: {
    padding: '24px'
  },
  modalFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    padding: '16px 24px',
    borderTop: '1px solid var(--border-color)',
    backgroundColor: 'var(--bg-secondary)'
  },
  formGroup: {
    marginBottom: '20px'
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '500',
    color: 'var(--text-primary)',
    marginBottom: '8px'
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: '8px',
    border: '1px solid var(--border-color)',
    backgroundColor: 'var(--bg-secondary)',
    color: 'var(--text-primary)',
    fontSize: '14px',
    outline: 'none',
    transition: 'border-color 0.2s',
    boxSizing: 'border-box'
  },
  select: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: '8px',
    border: '1px solid var(--border-color)',
    backgroundColor: 'var(--bg-secondary)',
    color: 'var(--text-primary)',
    fontSize: '14px',
    outline: 'none',
    cursor: 'pointer'
  },
  uploadZone: {
    border: '2px dashed var(--border-color)',
    borderRadius: '12px',
    padding: '40px',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s',
    backgroundColor: 'var(--bg-secondary)'
  },
  uploadZoneActive: {
    borderColor: 'var(--accent-primary)',
    backgroundColor: 'rgba(var(--accent-primary-rgb), 0.05)'
  },
  uploadIcon: {
    width: '48px',
    height: '48px',
    color: 'var(--text-secondary)',
    margin: '0 auto 16px'
  },
  uploadText: {
    fontSize: '16px',
    fontWeight: '500',
    color: 'var(--text-primary)',
    marginBottom: '8px'
  },
  uploadHint: {
    fontSize: '14px',
    color: 'var(--text-secondary)'
  },
  previewModal: {
    maxWidth: '900px'
  },
  previewContainer: {
    display: 'flex',
    gap: '24px'
  },
  previewMain: {
    flex: 1,
    backgroundColor: 'var(--bg-secondary)',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px',
    overflow: 'hidden'
  },
  previewImage: {
    maxWidth: '100%',
    maxHeight: '400px',
    objectFit: 'contain'
  },
  previewDetails: {
    width: '280px',
    flexShrink: 0
  },
  detailItem: {
    marginBottom: '16px'
  },
  detailLabel: {
    fontSize: '12px',
    fontWeight: '600',
    color: 'var(--text-secondary)',
    textTransform: 'uppercase',
    marginBottom: '4px'
  },
  detailValue: {
    fontSize: '14px',
    color: 'var(--text-primary)'
  },
  tagList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px'
  },
  tag: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: '12px',
    backgroundColor: 'var(--bg-tertiary)',
    color: 'var(--text-secondary)'
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    color: 'var(--text-secondary)'
  },
  emptyIcon: {
    width: '64px',
    height: '64px',
    margin: '0 auto 16px',
    opacity: 0.5
  },
  emptyTitle: {
    fontSize: '18px',
    fontWeight: '500',
    color: 'var(--text-primary)',
    marginBottom: '8px'
  },
  emptyText: {
    fontSize: '14px',
    marginBottom: '20px'
  },
  checkbox: {
    width: '18px',
    height: '18px',
    cursor: 'pointer'
  },
  selectedBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '12px 16px',
    backgroundColor: 'var(--accent-primary)',
    color: 'white',
    fontSize: '14px'
  },
  selectedBarActions: {
    display: 'flex',
    gap: '8px',
    marginLeft: 'auto'
  },
  filterDropdown: {
    position: 'absolute',
    top: '100%',
    right: 0,
    backgroundColor: 'var(--bg-primary)',
    borderRadius: '8px',
    border: '1px solid var(--border-color)',
    boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
    padding: '12px',
    minWidth: '200px',
    zIndex: 100
  },
  filterOption: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    color: 'var(--text-primary)',
    transition: 'background-color 0.2s'
  },
  storageInfo: {
    padding: '16px',
    borderTop: '1px solid var(--border-color)'
  },
  storageBar: {
    height: '6px',
    backgroundColor: 'var(--bg-tertiary)',
    borderRadius: '3px',
    overflow: 'hidden',
    marginBottom: '8px'
  },
  storageBarFill: {
    height: '100%',
    backgroundColor: 'var(--accent-primary)',
    borderRadius: '3px',
    transition: 'width 0.3s'
  },
  storageText: {
    fontSize: '12px',
    color: 'var(--text-secondary)'
  }
});

const ContentLibraryBrowser = ({ projectId, onSelect, selectionMode = false }) => {
  const { isDark } = useTheme();
  const styles = getStyles(isDark);
  const [files, setFiles] = useState([]);
  const [folders, setFolders] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [breadcrumb, setBreadcrumb] = useState([{ id: null, name: 'All Files' }]);
  const [viewMode, setViewMode] = useState('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [uploadDragActive, setUploadDragActive] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [uploadFiles, setUploadFiles] = useState([]);
  const [storageUsed, setStorageUsed] = useState(0);
  const [storageLimit] = useState(5 * 1024 * 1024 * 1024); // 5GB
  const [hoveredItem, setHoveredItem] = useState(null);

  const fileTypeIcons = {
    image: PhotoIcon,
    video: VideoCameraIcon,
    audio: MusicalNoteIcon,
    document: DocumentIcon,
    folder: FolderIcon
  };

  const fileTypeColors = {
    image: '#10b981',
    video: '#8b5cf6',
    audio: '#f59e0b',
    document: '#3b82f6',
    folder: '#6366f1'
  };

  const fetchContent = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        folder_id: currentFolder,
        search: searchQuery || undefined,
        type: filterType !== 'all' ? filterType : undefined
      };

      const [filesRes, foldersRes] = await Promise.all([
        api.get(`/documents/project/${projectId || 'all'}`, { params }),
        api.get('/documents/folders', { params: { parent_id: currentFolder } })
      ]);

      setFiles(filesRes.data || []);
      setFolders(foldersRes.data || []);
      
      // Calculate storage used
      const totalSize = (filesRes.data || []).reduce((sum, f) => sum + (f.size || 0), 0);
      setStorageUsed(totalSize);
    } catch (error) {
      console.error('Error fetching content:', error);
    } finally {
      setLoading(false);
    }
  }, [projectId, currentFolder, searchQuery, filterType]);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  const getFileType = (file) => {
    const ext = (file.filename || file.name || '').split('.').pop()?.toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) return 'image';
    if (['mp4', 'webm', 'mov', 'avi'].includes(ext)) return 'video';
    if (['mp3', 'wav', 'ogg', 'aac'].includes(ext)) return 'audio';
    return 'document';
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const formatDate = (date) => {
    if (!date) return 'Unknown';
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handleFolderClick = (folder) => {
    setCurrentFolder(folder.id);
    setBreadcrumb([...breadcrumb, { id: folder.id, name: folder.name }]);
    setSelectedFiles([]);
  };

  const handleBreadcrumbClick = (index) => {
    const item = breadcrumb[index];
    setCurrentFolder(item.id);
    setBreadcrumb(breadcrumb.slice(0, index + 1));
    setSelectedFiles([]);
  };

  const handleFileSelect = (file, event) => {
    if (event.ctrlKey || event.metaKey) {
      setSelectedFiles(prev =>
        prev.includes(file.id)
          ? prev.filter(id => id !== file.id)
          : [...prev, file.id]
      );
    } else if (selectionMode) {
      setSelectedFiles([file.id]);
      onSelect && onSelect(file);
    } else {
      setPreviewFile(file);
      setShowPreviewModal(true);
    }
  };

  const handleSelectAll = () => {
    if (selectedFiles.length === files.length) {
      setSelectedFiles([]);
    } else {
      setSelectedFiles(files.map(f => f.id));
    }
  };

  const handleDeleteSelected = async () => {
    if (!window.confirm(`Delete ${selectedFiles.length} file(s)?`)) return;
    try {
      await Promise.all(selectedFiles.map(id => api.delete(`/documents/${id}`)));
      setSelectedFiles([]);
      fetchContent();
    } catch (error) {
      console.error('Error deleting files:', error);
    }
  };

  const handleToggleFavorite = async (file, event) => {
    event.stopPropagation();
    try {
      await api.patch(`/documents/${file.id}`, { is_favorite: !file.is_favorite });
      fetchContent();
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  const handleDownload = async (file, event) => {
    event.stopPropagation();
    try {
      const response = await api.get(`/documents/${file.id}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', file.filename || file.name);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      await api.post('/documents/folders', {
        name: newFolderName,
        parent_id: currentFolder
      });
      setNewFolderName('');
      setShowFolderModal(false);
      fetchContent();
    } catch (error) {
      console.error('Error creating folder:', error);
    }
  };

  const handleUpload = async () => {
    if (uploadFiles.length === 0) return;
    try {
      const formData = new FormData();
      uploadFiles.forEach(file => formData.append('files', file));
      if (currentFolder) formData.append('folder_id', currentFolder);
      if (projectId) formData.append('project_id', projectId);

      await api.post('/documents/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setUploadFiles([]);
      setShowUploadModal(false);
      fetchContent();
    } catch (error) {
      console.error('Error uploading files:', error);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setUploadDragActive(true);
  };

  const handleDragLeave = () => {
    setUploadDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setUploadDragActive(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    setUploadFiles(prev => [...prev, ...droppedFiles]);
  };

  const handleFileInputChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setUploadFiles(prev => [...prev, ...selectedFiles]);
  };

  const FileTypeIcon = ({ type }) => {
    const Icon = fileTypeIcons[type] || DocumentIcon;
    return <Icon style={{ ...styles.fileTypeIcon, color: fileTypeColors[type] }} />;
  };

  const renderGridView = () => (
    <div style={styles.contentGrid}>
      {folders.map(folder => (
        <div
          key={`folder-${folder.id}`}
          style={{
            ...styles.fileCard,
            ...(hoveredItem === `folder-${folder.id}` ? { transform: 'translateY(-2px)', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' } : {})
          }}
          onClick={() => handleFolderClick(folder)}
          onMouseEnter={() => setHoveredItem(`folder-${folder.id}`)}
          onMouseLeave={() => setHoveredItem(null)}
        >
          <div style={styles.filePreview}>
            <FolderIcon style={{ ...styles.fileTypeIcon, color: fileTypeColors.folder }} />
          </div>
          <div style={styles.fileInfo}>
            <div style={styles.fileName}>{folder.name}</div>
            <div style={styles.fileMeta}>
              <span>{folder.file_count || 0} items</span>
            </div>
          </div>
        </div>
      ))}

      {files.map(file => {
        const fileType = getFileType(file);
        const isSelected = selectedFiles.includes(file.id);
        const isHovered = hoveredItem === `file-${file.id}`;

        return (
          <div
            key={`file-${file.id}`}
            style={{
              ...styles.fileCard,
              ...(isSelected ? styles.fileCardSelected : {}),
              ...(isHovered ? { transform: 'translateY(-2px)', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' } : {})
            }}
            onClick={(e) => handleFileSelect(file, e)}
            onMouseEnter={() => setHoveredItem(`file-${file.id}`)}
            onMouseLeave={() => setHoveredItem(null)}
          >
            <div style={styles.filePreview}>
              {fileType === 'image' && file.url ? (
                <img src={file.url} alt={file.filename} style={styles.filePreviewImage} />
              ) : (
                <FileTypeIcon type={fileType} />
              )}
              <button
                style={styles.starBtn}
                onClick={(e) => handleToggleFavorite(file, e)}
              >
                {file.is_favorite ? (
                  <StarSolidIcon style={{ width: '16px', height: '16px', color: '#fbbf24' }} />
                ) : (
                  <StarIcon style={{ width: '16px', height: '16px', color: 'white' }} />
                )}
              </button>
              <div style={{ ...styles.fileActions, opacity: isHovered ? 1 : 0 }}>
                <button style={styles.fileActionBtn} onClick={(e) => { e.stopPropagation(); setPreviewFile(file); setShowPreviewModal(true); }}>
                  <EyeIcon style={{ width: '14px', height: '14px' }} />
                </button>
                <button style={styles.fileActionBtn} onClick={(e) => handleDownload(file, e)}>
                  <ArrowDownTrayIcon style={{ width: '14px', height: '14px' }} />
                </button>
              </div>
            </div>
            <div style={styles.fileInfo}>
              <div style={styles.fileName}>{file.filename || file.name}</div>
              <div style={styles.fileMeta}>
                <span>{formatFileSize(file.size)}</span>
                <span>•</span>
                <span>{formatDate(file.created_at)}</span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  const renderListView = () => (
    <div style={styles.contentList}>
      {folders.map(folder => (
        <div
          key={`folder-${folder.id}`}
          style={{
            ...styles.listItem,
            ...(hoveredItem === `folder-${folder.id}` ? styles.listItemHover : {})
          }}
          onClick={() => handleFolderClick(folder)}
          onMouseEnter={() => setHoveredItem(`folder-${folder.id}`)}
          onMouseLeave={() => setHoveredItem(null)}
        >
          <div style={{ ...styles.listItemIcon, backgroundColor: `${fileTypeColors.folder}20` }}>
            <FolderIcon style={{ width: '20px', height: '20px', color: fileTypeColors.folder }} />
          </div>
          <div style={styles.listItemInfo}>
            <div style={styles.listItemName}>{folder.name}</div>
            <div style={styles.listItemMeta}>{folder.file_count || 0} items</div>
          </div>
          <ChevronRightIcon style={{ width: '20px', height: '20px', color: 'var(--text-secondary)' }} />
        </div>
      ))}

      {files.map(file => {
        const fileType = getFileType(file);
        const isSelected = selectedFiles.includes(file.id);
        const isHovered = hoveredItem === `file-${file.id}`;

        return (
          <div
            key={`file-${file.id}`}
            style={{
              ...styles.listItem,
              ...(isSelected ? styles.listItemSelected : {}),
              ...(isHovered ? styles.listItemHover : {})
            }}
            onClick={(e) => handleFileSelect(file, e)}
            onMouseEnter={() => setHoveredItem(`file-${file.id}`)}
            onMouseLeave={() => setHoveredItem(null)}
          >
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => {}}
              onClick={(e) => e.stopPropagation()}
              style={styles.checkbox}
            />
            <div style={{ ...styles.listItemIcon, backgroundColor: `${fileTypeColors[fileType]}20` }}>
              {React.createElement(fileTypeIcons[fileType] || DocumentIcon, {
                style: { width: '20px', height: '20px', color: fileTypeColors[fileType] }
              })}
            </div>
            <div style={styles.listItemInfo}>
              <div style={styles.listItemName}>{file.filename || file.name}</div>
              <div style={styles.listItemMeta}>
                {formatFileSize(file.size)} • {formatDate(file.created_at)}
              </div>
            </div>
            <div style={styles.listItemActions}>
              <button style={styles.btnIcon} onClick={(e) => handleToggleFavorite(file, e)}>
                {file.is_favorite ? (
                  <StarSolidIcon style={{ width: '16px', height: '16px', color: '#fbbf24' }} />
                ) : (
                  <StarIcon style={{ width: '16px', height: '16px', color: 'var(--text-secondary)' }} />
                )}
              </button>
              <button style={styles.btnIcon} onClick={(e) => handleDownload(file, e)}>
                <ArrowDownTrayIcon style={{ width: '16px', height: '16px', color: 'var(--text-secondary)' }} />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <div>
            <h2 style={styles.title}>Content Library</h2>
            <p style={styles.subtitle}>Manage and organize your media assets</p>
          </div>
          <div style={styles.headerActions}>
            <button
              style={{ ...styles.btn, ...styles.btnSecondary }}
              onClick={() => setShowFolderModal(true)}
            >
              <FolderPlusIcon style={{ width: '18px', height: '18px' }} />
              New Folder
            </button>
            <button
              style={{ ...styles.btn, ...styles.btnPrimary }}
              onClick={() => setShowUploadModal(true)}
            >
              <CloudArrowUpIcon style={{ width: '18px', height: '18px' }} />
              Upload Files
            </button>
          </div>
        </div>

      <div style={styles.mainContent}>
        {/* Sidebar */}
        <div style={styles.sidebar}>
          <div style={styles.sidebarSection}>
            <div style={styles.sidebarTitle}>Quick Access</div>
            {[
              { id: null, name: 'All Files', icon: FolderIcon, count: files.length },
              { id: 'recent', name: 'Recent', icon: ClockIcon, count: null },
              { id: 'favorites', name: 'Favorites', icon: StarIcon, count: files.filter(f => f.is_favorite).length },
            ].map(item => (
              <div
                key={item.id || 'all'}
                style={{
                  ...styles.folderItem,
                  ...(currentFolder === item.id ? styles.folderItemActive : {}),
                  ...(hoveredItem === `nav-${item.id}` && currentFolder !== item.id ? styles.folderItemHover : {})
                }}
                onMouseEnter={() => setHoveredItem(`nav-${item.id}`)}
                onMouseLeave={() => setHoveredItem(null)}
                onClick={() => {
                  setCurrentFolder(item.id);
                  setBreadcrumb([{ id: item.id, name: item.name }]);
                }}
              >
                <item.icon style={styles.folderIcon} />
                {item.name}
                {item.count !== null && (
                  <span style={styles.folderCount}>{item.count}</span>
                )}
              </div>
            ))}
          </div>

          <div style={styles.sidebarSection}>
            <div style={styles.sidebarTitle}>File Types</div>
            {[
              { type: 'all', name: 'All Types', icon: FolderIcon },
              { type: 'image', name: 'Images', icon: PhotoIcon },
              { type: 'video', name: 'Videos', icon: VideoCameraIcon },
              { type: 'audio', name: 'Audio', icon: MusicalNoteIcon },
              { type: 'document', name: 'Documents', icon: DocumentIcon },
            ].map(item => (
              <div
                key={item.type}
                style={{
                  ...styles.folderItem,
                  ...(filterType === item.type ? styles.folderItemActive : {}),
                  ...(hoveredItem === `type-${item.type}` && filterType !== item.type ? styles.folderItemHover : {})
                }}
                onMouseEnter={() => setHoveredItem(`type-${item.type}`)}
                onMouseLeave={() => setHoveredItem(null)}
                onClick={() => setFilterType(item.type)}
              >
                <item.icon style={styles.folderIcon} />
                {item.name}
              </div>
            ))}
          </div>

          <div style={styles.storageInfo}>
            <div style={styles.storageBar}>
              <div
                style={{
                  ...styles.storageBarFill,
                  width: `${(storageUsed / storageLimit) * 100}%`
                }}
              />
            </div>
            <div style={styles.storageText}>
              {formatFileSize(storageUsed)} of {formatFileSize(storageLimit)} used
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div style={styles.contentArea}>
          <div style={styles.toolbar}>
            <div style={styles.searchContainer}>
              <MagnifyingGlassIcon style={{ width: '18px', height: '18px', color: 'var(--text-secondary)' }} />
              <input
                type="text"
                placeholder="Search files..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={styles.searchInput}
              />
            </div>
            <div style={styles.toolbarActions}>
              <button
                style={styles.btnIcon}
                onClick={fetchContent}
              >
                <ArrowPathIcon style={{ width: '18px', height: '18px', color: 'var(--text-secondary)' }} />
              </button>
              <div style={styles.viewToggle}>
                <button
                  style={{
                    ...styles.viewToggleBtn,
                    ...(viewMode === 'grid' ? styles.viewToggleBtnActive : {})
                  }}
                  onClick={() => setViewMode('grid')}
                >
                  <Squares2X2Icon style={{ width: '18px', height: '18px' }} />
                </button>
                <button
                  style={{
                    ...styles.viewToggleBtn,
                    ...(viewMode === 'list' ? styles.viewToggleBtnActive : {})
                  }}
                  onClick={() => setViewMode('list')}
                >
                  <ListBulletIcon style={{ width: '18px', height: '18px' }} />
                </button>
              </div>
            </div>
          </div>

          {/* Breadcrumb */}
          <div style={styles.breadcrumb}>
            {breadcrumb.map((item, index) => (
              <React.Fragment key={item.id || 'root'}>
                {index > 0 && <ChevronRightIcon style={{ width: '14px', height: '14px', ...styles.breadcrumbSeparator }} />}
                <span
                  style={{
                    ...styles.breadcrumbItem,
                    ...(index === breadcrumb.length - 1 ? styles.breadcrumbItemActive : {})
                  }}
                  onClick={() => index < breadcrumb.length - 1 && handleBreadcrumbClick(index)}
                >
                  {item.name}
                </span>
              </React.Fragment>
            ))}
          </div>

          {/* Selected Files Bar */}
          {selectedFiles.length > 0 && (
            <div style={styles.selectedBar}>
              <CheckIcon style={{ width: '18px', height: '18px' }} />
              <span>{selectedFiles.length} file(s) selected</span>
              <div style={styles.selectedBarActions}>
                <button style={{ ...styles.btn, backgroundColor: 'rgba(255,255,255,0.2)', color: 'white' }} onClick={handleSelectAll}>
                  {selectedFiles.length === files.length ? 'Deselect All' : 'Select All'}
                </button>
                <button style={{ ...styles.btn, backgroundColor: '#ef4444', color: 'white' }} onClick={handleDeleteSelected}>
                  <TrashIcon style={{ width: '16px', height: '16px' }} />
                  Delete
                </button>
              </div>
            </div>
          )}

          {/* Content */}
          {loading ? (
            <div style={styles.emptyState}>
              <ArrowPathIcon style={{ ...styles.emptyIcon, animation: 'spin 1s linear infinite' }} />
              <p>Loading content...</p>
            </div>
          ) : files.length === 0 && folders.length === 0 ? (
            <div style={styles.emptyState}>
              <FolderIcon style={styles.emptyIcon} />
              <div style={styles.emptyTitle}>No files found</div>
              <p style={styles.emptyText}>
                {searchQuery ? 'Try adjusting your search' : 'Upload files to get started'}
              </p>
              <button
                style={{ ...styles.btn, ...styles.btnPrimary }}
                onClick={() => setShowUploadModal(true)}
              >
                <CloudArrowUpIcon style={{ width: '18px', height: '18px' }} />
                Upload Files
              </button>
            </div>
          ) : viewMode === 'grid' ? renderGridView() : renderListView()}
        </div>
      </div>
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div style={styles.modal} onClick={() => setShowUploadModal(false)}>
          <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>Upload Files</h3>
              <button style={styles.btnIcon} onClick={() => setShowUploadModal(false)}>
                <XMarkIcon style={{ width: '20px', height: '20px', color: 'var(--text-secondary)' }} />
              </button>
            </div>
            <div style={styles.modalBody}>
              <div
                style={{
                  ...styles.uploadZone,
                  ...(uploadDragActive ? styles.uploadZoneActive : {})
                }}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => document.getElementById('file-input').click()}
              >
                <CloudArrowUpIcon style={styles.uploadIcon} />
                <div style={styles.uploadText}>Drag and drop files here</div>
                <div style={styles.uploadHint}>or click to browse</div>
                <input
                  id="file-input"
                  type="file"
                  multiple
                  onChange={handleFileInputChange}
                  style={{ display: 'none' }}
                />
              </div>

              {uploadFiles.length > 0 && (
                <div style={{ marginTop: '20px' }}>
                  <div style={styles.sidebarTitle}>{uploadFiles.length} file(s) selected</div>
                  {uploadFiles.map((file, index) => (
                    <div key={index} style={{ ...styles.listItem, padding: '8px 0', borderBottom: 'none' }}>
                      <DocumentIcon style={{ width: '20px', height: '20px', color: 'var(--text-secondary)' }} />
                      <div style={styles.listItemInfo}>
                        <div style={styles.listItemName}>{file.name}</div>
                        <div style={styles.listItemMeta}>{formatFileSize(file.size)}</div>
                      </div>
                      <button
                        style={styles.btnIcon}
                        onClick={() => setUploadFiles(prev => prev.filter((_, i) => i !== index))}
                      >
                        <XMarkIcon style={{ width: '16px', height: '16px', color: 'var(--text-secondary)' }} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div style={styles.modalFooter}>
              <button style={{ ...styles.btn, ...styles.btnSecondary }} onClick={() => setShowUploadModal(false)}>
                Cancel
              </button>
              <button
                style={{ ...styles.btn, ...styles.btnPrimary }}
                onClick={handleUpload}
                disabled={uploadFiles.length === 0}
              >
                Upload {uploadFiles.length > 0 && `(${uploadFiles.length})`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Folder Modal */}
      {showFolderModal && (
        <div style={styles.modal} onClick={() => setShowFolderModal(false)}>
          <div style={{ ...styles.modalContent, maxWidth: '400px' }} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>Create New Folder</h3>
              <button style={styles.btnIcon} onClick={() => setShowFolderModal(false)}>
                <XMarkIcon style={{ width: '20px', height: '20px', color: 'var(--text-secondary)' }} />
              </button>
            </div>
            <div style={styles.modalBody}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Folder Name</label>
                <input
                  type="text"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  placeholder="Enter folder name"
                  style={styles.input}
                  autoFocus
                />
              </div>
            </div>
            <div style={styles.modalFooter}>
              <button style={{ ...styles.btn, ...styles.btnSecondary }} onClick={() => setShowFolderModal(false)}>
                Cancel
              </button>
              <button
                style={{ ...styles.btn, ...styles.btnPrimary }}
                onClick={handleCreateFolder}
                disabled={!newFolderName.trim()}
              >
                Create Folder
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreviewModal && previewFile && (
        <div style={styles.modal} onClick={() => setShowPreviewModal(false)}>
          <div style={{ ...styles.modalContent, ...styles.previewModal }} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>{previewFile.filename || previewFile.name}</h3>
              <button style={styles.btnIcon} onClick={() => setShowPreviewModal(false)}>
                <XMarkIcon style={{ width: '20px', height: '20px', color: 'var(--text-secondary)' }} />
              </button>
            </div>
            <div style={styles.modalBody}>
              <div style={styles.previewContainer}>
                <div style={styles.previewMain}>
                  {getFileType(previewFile) === 'image' && previewFile.url ? (
                    <img src={previewFile.url} alt={previewFile.filename} style={styles.previewImage} />
                  ) : (
                    <FileTypeIcon type={getFileType(previewFile)} />
                  )}
                </div>
                <div style={styles.previewDetails}>
                  <div style={styles.detailItem}>
                    <div style={styles.detailLabel}>File Name</div>
                    <div style={styles.detailValue}>{previewFile.filename || previewFile.name}</div>
                  </div>
                  <div style={styles.detailItem}>
                    <div style={styles.detailLabel}>File Size</div>
                    <div style={styles.detailValue}>{formatFileSize(previewFile.size)}</div>
                  </div>
                  <div style={styles.detailItem}>
                    <div style={styles.detailLabel}>Type</div>
                    <div style={styles.detailValue}>{getFileType(previewFile).toUpperCase()}</div>
                  </div>
                  <div style={styles.detailItem}>
                    <div style={styles.detailLabel}>Created</div>
                    <div style={styles.detailValue}>{formatDate(previewFile.created_at)}</div>
                  </div>
                  <div style={styles.detailItem}>
                    <div style={styles.detailLabel}>Modified</div>
                    <div style={styles.detailValue}>{formatDate(previewFile.updated_at)}</div>
                  </div>
                  {previewFile.tags && previewFile.tags.length > 0 && (
                    <div style={styles.detailItem}>
                      <div style={styles.detailLabel}>Tags</div>
                      <div style={styles.tagList}>
                        {previewFile.tags.map((tag, index) => (
                          <span key={index} style={styles.tag}>
                            <TagIcon style={{ width: '12px', height: '12px' }} />
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  <div style={{ marginTop: '24px', display: 'flex', gap: '8px' }}>
                    <button
                      style={{ ...styles.btn, ...styles.btnPrimary, flex: 1 }}
                      onClick={(e) => handleDownload(previewFile, e)}
                    >
                      <ArrowDownTrayIcon style={{ width: '16px', height: '16px' }} />
                      Download
                    </button>
                    <button
                      style={{ ...styles.btn, ...styles.btnSecondary }}
                      onClick={() => {
                        navigator.clipboard.writeText(previewFile.url);
                      }}
                    >
                      <LinkIcon style={{ width: '16px', height: '16px' }} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContentLibraryBrowser;
